import { Helmet } from 'react-helmet';
import { useState, useEffect, useCallback, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { Card } from '../../components/ui/card';
import { Building2, Users, Home, PhoneCall, Plus } from 'lucide-react';
import { Button } from '../../components/ui/button';
import PropertyTable from '../../components/PropertyTable';
import PropertyDetail from '../../components/PropertyDetail';
import { getDashboardStats, getProperties } from '../../lib/apiService';
import { Chart as ChartJS, ArcElement, Tooltip, Legend } from 'chart.js';
import { Pie } from 'react-chartjs-2';

ChartJS.register(ArcElement, Tooltip, Legend);

const AdminDashboard = () => {
  const { userProfile } = useAuth();
  const navigate = useNavigate();
  const [stats, setStats] = useState(null);
  const [properties, setProperties] = useState([]);
  const [selectedProperty, setSelectedProperty] = useState(null);
  const [loading, setLoading] = useState(true);

  const fetchData = useCallback(async () => {
    if (!userProfile) return;

    try {
      const [statsRes, propsRes] = await Promise.all([
        getDashboardStats(userProfile),
        getProperties({ currentUser: userProfile })
      ]);

      setStats(statsRes?.stats || statsRes);
      setProperties(propsRes.properties || []);
    } catch (error) {
      console.error('Failed to fetch data:', error);
    } finally {
      setLoading(false);
    }
  }, [userProfile]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const statCards = [
    {
      icon: Building2,
      label: 'Active Properties',
      value: stats?.total_active_properties ?? stats?.total_properties ?? 0,
      bg: 'bg-emerald-50',
      text: 'text-emerald-600'
    },
    {
      icon: Users,
      label: 'Total Users',
      value: stats?.total_users ?? 0,
      bg: 'bg-purple-50',
      text: 'text-purple-600'
    },
    {
      icon: Home,
      label: 'Total PG',
      value: stats?.total_pg ?? 0,
      bg: 'bg-orange-50',
      text: 'text-orange-600'
    },
    {
      icon: PhoneCall,
      label: 'Total Leads',
      value: stats?.total_leads ?? 0,
      bg: 'bg-blue-50',
      text: 'text-blue-600'
    }
  ];

  const pieChartData = useMemo(() => {
    const counts = {
      Flat: 0,
      House: 0,
      Plot: 0,
      Building: 0,
      Appartment: 0,
      PG: 0,
      Hostel: 0,
      Other: 0
    };

    properties.forEach(p => {
      const type = p.property_type;
      if (counts[type] !== undefined) {
        counts[type]++;
      } else if (type === 'Appartment') { // Appartment is sometimes misspelled
        counts['Appartment']++;
      } else {
        counts['Other']++;
      }
    });

    const labels = [];
    const data = [];
    const bgColors = [];

    const colorPalette = {
      Flat: '#3b82f6',      // blue
      House: '#10b981',     // emerald-500
      Plot: '#f59e0b',      // amber-500
      Building: '#3b82f6',  // blue-500
      Appartment: '#8b5cf6',// violet-500
      PG: '#ec4899',        // pink-500
      Hostel: '#f43f5e',    // rose-500
      Other: '#94a3b8'      // slate-400
    };

    Object.keys(counts).forEach(key => {
      if (counts[key] > 0) {
        labels.push(key);
        data.push(counts[key]);
        bgColors.push(colorPalette[key]);
      }
    });

    return {
      labels,
      datasets: [
        {
          data,
          backgroundColor: bgColors,
          borderWidth: 0,
        },
      ],
    };
  }, [properties]);

  if (loading) {
    return (
      <div className="flex items-center justify-center h-screen">
        <div className="w-8 h-8 border-4 border-[#ee7923] border-t-transparent rounded-full animate-spin"></div>
      </div>
    );
  }

  return (
    <div className="p-6 lg:p-8">
      <Helmet>
        <title>Dashboard | JoldiPabo Admin</title>
      </Helmet>
      <div className="mb-8">
        <h1 className="text-2xl sm:text-3xl font-semibold tracking-tight text-slate-900" style={{ fontFamily: 'Outfit, sans-serif' }}>
          Dashboard
        </h1>
        <p className="text-sm text-slate-600 mt-1">Complete overview of your real estate portfolio</p>
      </div>

      {/* KPI Cards */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
        {statCards.map((stat, idx) => {
          const Icon = stat.icon;
          return (
            <Card key={idx} className="p-5 shadow-sm border border-slate-200">
              <div className="flex items-start justify-between">
                <div>
                  <p className="text-xs font-semibold text-slate-500 uppercase tracking-wide">{stat.label}</p>
                  <p className="text-2xl font-bold text-slate-900 mt-2">{stat.value}</p>
                </div>
                <div className={`p-3 rounded-xl ${stat.bg}`}>
                  <Icon className={`w-5 h-5 ${stat.text}`} />
                </div>
              </div>
            </Card>
          );
        })}
      </div>

      <div className="grid grid-cols-1 md:grid-cols-12 gap-6">
        {/* Properties Table (col-span-9) */}
        <div className="md:col-span-9">
          <Card className="p-6 h-full">
            <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4 mb-6">
              <h2 className="text-lg sm:text-xl font-semibold tracking-tight text-slate-900" style={{ fontFamily: 'Outfit, sans-serif' }}>
                Properties
              </h2>
              <Button
                onClick={() => navigate('/add-property')}
                className="bg-slate-900 hover:bg-slate-800"
                data-testid="add-property-button"
              >
                <Plus className="w-4 h-4 mr-2" />
                Add Property
              </Button>
            </div>

            <PropertyTable
              properties={properties
                .slice()
                .sort((a, b) => (b.views_count || 0) - (a.views_count || 0))
                .slice(0, 5)}
              onSelectProperty={setSelectedProperty}
              onRefresh={fetchData}
            />
          </Card>
        </div>

        {/* Property Type Pie Chart (col-span-3) */}
        <div className="md:col-span-3">
          <Card className="p-6 h-fit flex flex-col">
            <h2 className="text-base font-semibold tracking-tight text-slate-900 mb-6" style={{ fontFamily: 'Outfit, sans-serif' }}>
              Property Distribution
            </h2>
            <div className="flex-1 flex items-center justify-center">
              {pieChartData.labels.length > 0 ? (
                <div className="w-full max-w-[200px] aspect-square">
                  <Pie
                    data={pieChartData}
                    options={{
                      plugins: {
                        tooltip: {
                          callbacks: {
                            label: (context) => {
                              return `Properties: ${context.parsed}`;
                            }
                          }
                        },
                        legend: {
                          display: false
                        }
                      },
                      cutout: '60%',
                      maintainAspectRatio: true
                    }}
                  />
                </div>
              ) : (
                <p className="text-sm text-slate-500 text-center">No properties found</p>
              )}
            </div>
          </Card>
        </div>
      </div>

      {/* Property Detail Side Sheet */}
      {selectedProperty && (
        <PropertyDetail
          property={selectedProperty}
          onClose={() => setSelectedProperty(null)}
          onRefresh={fetchData}
        />
      )}
    </div>
  );
};

export default AdminDashboard;
